import { useState, useEffect, useCallback, useRef } from 'react';
import { parseTokenAmount, parseCSBTCAmount } from '../utils/helpers';
import { tokenPriceService } from '../services/tokenPriceService';

export const useTokens = (currentWallet) => {
  const [userTokens, setUserTokens] = useState([]);
  const [popularTokens, setPopularTokens] = useState([]);
  const [prices, setPrices] = useState({
    solPrice: 0,
    csbtcPrice: 0,
    csbtcPriceChange24h: 0,
    csbtcVolume24h: 0
  });
  const [loading, setLoading] = useState(false);
  const [walletData, setWalletData] = useState({
    solBalance: 0,
    csbtcBalance: 0,
    solValueUSD: 0,
    csbtcValueUSD: 0,
    totalValueUSD: 0
  });

  // Referencias para evitar reset innecesario
  const lastValidTokensRef = useRef([]);
  const lastPublicKeyRef = useRef('');
  const isMountedRef = useRef(true);

  // Obtener precios de tokens desde múltiples fuentes
  const loadTokenPrices = useCallback(async () => {
    try {
      console.log('💰 Cargando precios desde múltiples APIs...');
      const priceData = await tokenPriceService.getAllPrices();

      // Validar precios recibidos
      const validatedPrices = {
        solPrice: priceData.solPrice > 0 ? priceData.solPrice : 100,
        csbtcPrice: priceData.csbtcPrice > 0 ? priceData.csbtcPrice : 0.001261,
        csbtcPriceChange24h: priceData.csbtcPriceChange24h || 0,
        csbtcVolume24h: priceData.csbtcVolume24h || 0
      };

      console.log('💰 Precios cargados:', validatedPrices);
      setPrices(validatedPrices);
      return validatedPrices;
    } catch (error) {
      console.error('Error loading token prices:', error);

      // Fallback con precios realistas
      const fallbackPrices = {
        solPrice: 100,
        csbtcPrice: 0.001261,
        csbtcPriceChange24h: 0,
        csbtcVolume24h: 0
      };

      console.log('💰 Usando precios de fallback:', fallbackPrices);
      setPrices(fallbackPrices);
      return fallbackPrices;
    }
  }, []);

  // Procesar y formatear tokens para la UI
  const processTokensForUI = useCallback((tokens, priceData) => {
    if (!tokens || !Array.isArray(tokens)) {
      console.warn('⚠️ tokens no es un array válido:', tokens);
      return [];
    }

    const processed = tokens.map(token => {
      if (!token) {
        console.warn('⚠️ Token inválido encontrado');
        return null;
      }

      let symbol = token.symbol || 'UNKNOWN';
      let name = token.name || 'Unknown Token';
      let price = 0;
      let valueUSD = 0;
      let priceChange24h = 0;

      // Identificar tokens conocidos
      const tokenMint = token.mint?.toString();

      if (tokenMint === '9EpY5vX7Jwc9z9v6un8HEpRuGWrMvWenVvcM1h1EXpmR') {
        symbol = 'CSBTC';
        name = 'Crosschain Bitcoin';
        price = priceData.csbtcPrice;
        priceChange24h = priceData.csbtcPriceChange24h;
      } else if (tokenMint === 'So11111111111111111111111111111111111111112') {
        symbol = 'SOL';
        name = 'Solana';
        price = priceData.solPrice;
      } else if (tokenMint === 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v') {
        symbol = 'USDC';
        name = 'USD Coin';
        price = 1;
      } else if (tokenMint === 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB') {
        symbol = 'USDT';
        name = 'Tether USD';
        price = 1;
      }

      // Calcular UI Amount
      let uiAmount = token.uiAmount;
      if ((!uiAmount || uiAmount === 0) && token.amount && token.decimals !== undefined) {
        uiAmount = parseTokenAmount(token.amount, token.decimals);
      }

      // Calcular valor en USD
      if (price > 0 && uiAmount) {
        valueUSD = uiAmount * price;
      }

      return {
        mint: tokenMint,
        symbol: symbol,
        name: name,
        amount: token.amount,
        uiAmount: uiAmount || 0,
        decimals: token.decimals || 6,
        price: price,
        valueUSD: valueUSD,
        priceChange24h: priceChange24h,
        hasPrice: price > 0,
        isKnownToken: symbol !== 'UNKNOWN',
        isCSBTC: symbol === 'CSBTC'
      };
    }).filter(token => token !== null);

    return processed;
  }, []);

  // Función para buscar CSBTC específicamente
  const findCSBTC = useCallback(async (publicKey) => {
    if (!publicKey) {
      console.log('❌ No hay publicKey para buscar CSBTC');
      return null;
    }

    try {
      console.log('🔍 Búsqueda específica de CSBTC para:', publicKey);

      const debugResponse = await fetch('/debug-tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ publicKey: publicKey.toString() }),
      });

      if (debugResponse.ok) {
        const debugData = await debugResponse.json();
        const csbtcAccount = debugData.accounts?.find(acc =>
          acc.mint === '9EpY5vX7Jwc9z9v6un8HEpRuGWrMvWenVvcM1h1EXpmR'
        );

        if (csbtcAccount) {
          console.log('🎯 CSBTC encontrado en debug-tokens:', csbtcAccount);
          const csbtcBalance = parseCSBTCAmount(csbtcAccount.amount);

          return {
            mint: '9EpY5vX7Jwc9z9v6un8HEpRuGWrMvWenVvcM1h1EXpmR',
            symbol: 'CSBTC',
            name: 'Crosschain Bitcoin',
            amount: csbtcAccount.amount,
            uiAmount: csbtcBalance,
            decimals: 6,
            isCSBTC: true
          };
        } else {
          console.log('❌ CSBTC no encontrado en debug-tokens');
        }
      } else {
        console.warn('⚠️ debug-tokens response not OK:', debugResponse.status);
      }
    } catch (error) {
      console.error('Error en búsqueda específica de CSBTC:', error);
    }

    return null;
  }, []);

  // Función para obtener SOL balance de manera segura
  const getSolBalanceSafe = useCallback(async (publicKey) => {
    try {
      const balanceResponse = await fetch('/balance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ publicKey: publicKey.toString() }),
      });

      if (balanceResponse.ok) {
        const balanceData = await balanceResponse.json();
        const solBalance = parseFloat(balanceData.balance) || 0;
        console.log('💰 Balance SOL obtenido:', solBalance);
        return solBalance;
      } else {
        console.warn('⚠️ Balance endpoint returned error:', balanceResponse.status);
        return 0;
      }
    } catch (error) {
      console.error('Error obteniendo balance SOL:', error);
      return 0;
    }
  }, []);

  // Función para obtener tokens de manera segura
  const getTokensSafe = useCallback(async (publicKey) => {
    try {
      const response = await fetch('/tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ publicKey: publicKey.toString() }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Tokens del backend:', data.tokens?.length || 0);
        return data.tokens || [];
      } else {
        console.warn('⚠️ Tokens endpoint returned error:', response.status);
        return [];
      }
    } catch (error) {
      console.error('Error obteniendo tokens del backend:', error);
      return [];
    }
  }, []);

  // Obtener tokens del usuario - VERSIÓN CORREGIDA SIN DEPENDENCIA CIRCULAR
  const getUserTokens = useCallback(async (publicKey = null) => {
    const targetPublicKey = publicKey || (currentWallet && currentWallet.publicKey);

    if (!targetPublicKey) {
      console.log('❌ No hay publicKey para obtener tokens');
      return; // NO limpiar tokens si no hay publicKey
    }

    // Guardar tokens actuales como backup usando ref para evitar dependencia
    const backupTokens = [...lastValidTokensRef.current];

    setLoading(true);

    try {
      console.log('🔄 Obteniendo tokens para:', targetPublicKey.toString());
      lastPublicKeyRef.current = targetPublicKey;

      // Obtener datos en paralelo pero con manejo de errores individual
      const [solBalance, backendTokens] = await Promise.allSettled([
        getSolBalanceSafe(targetPublicKey),
        getTokensSafe(targetPublicKey)
      ]);

      // Procesar SOL balance
      const solBalanceValue = solBalance.status === 'fulfilled' ? solBalance.value : 0;

      // Procesar tokens del backend
      let processedTokens = [];
      if (backendTokens.status === 'fulfilled') {
        processedTokens = backendTokens.value.map(token => {
          let uiAmount = token.uiAmount;
          if (!uiAmount || uiAmount === 0) {
            uiAmount = parseTokenAmount(token.amount, token.decimals || 6);
          }

          return {
            ...token,
            uiAmount: uiAmount,
            isCSBTC: token.mint === '9EpY5vX7Jwc9z9v6un8HEpRuGWrMvWenVvcM1h1EXpmR'
          };
        });
      }

      // Solo buscar CSBTC específicamente si no está en la lista y tenemos balance de SOL
      const hasCSBTC = processedTokens.some(token => token.isCSBTC);
      if (!hasCSBTC && solBalanceValue > 0) {
        console.log('🔍 Búsqueda específica de CSBTC...');
        const csbtcToken = await findCSBTC(targetPublicKey);
        if (csbtcToken && csbtcToken.uiAmount > 0) {
          console.log('✅ CSBTC agregado desde búsqueda específica');
          processedTokens.unshift(csbtcToken);
        }
      }

      // Cargar precios (esto NO debe fallar la operación completa)
      let priceData;
      try {
        priceData = await loadTokenPrices();
      } catch (priceError) {
        console.error('Error cargando precios:', priceError);
        priceData = prices; // Usar precios actuales como fallback
      }

      // Procesar tokens para UI
      const finalTokens = processTokensForUI(processedTokens, priceData);

      // Filtrar tokens con balance > 0
      const tokensWithBalance = finalTokens.filter(token => token.uiAmount > 0);

      // Calcular valores en USD
      const csbtcToken = finalTokens.find(token => token.isCSBTC);
      const csbtcBalance = csbtcToken?.uiAmount || 0;
      const csbtcValueUSD = csbtcBalance * priceData.csbtcPrice;
      const solValueUSD = solBalanceValue * priceData.solPrice;
      const totalValueUSD = solValueUSD + csbtcValueUSD;

      // IMPORTANTE: Si la nueva lista está vacía pero teníamos tokens antes,
      // y la publicKey es la misma, NO actualizar
      if (tokensWithBalance.length === 0 && backupTokens.length > 0 &&
          lastPublicKeyRef.current === targetPublicKey) {
        console.warn('⚠️ Tokens vacíos pero había tokens antes. Manteniendo tokens anteriores.');
        // Mantener los tokens anteriores y solo actualizar precios
        const updatedTokens = backupTokens.map(token => ({
          ...token,
          price: token.symbol === 'CSBTC' ? priceData.csbtcPrice :
                 token.symbol === 'SOL' ? priceData.solPrice : token.price,
          valueUSD: token.uiAmount * (token.symbol === 'CSBTC' ? priceData.csbtcPrice :
                                     token.symbol === 'SOL' ? priceData.solPrice : token.price || 1)
        }));

        setUserTokens(updatedTokens);
        lastValidTokensRef.current = updatedTokens;
      } else {
        // Actualizar con nuevos tokens
        setUserTokens(tokensWithBalance);
        lastValidTokensRef.current = tokensWithBalance;
      }

      // Actualizar walletData siempre
      setWalletData({
        solBalance: solBalanceValue,
        csbtcBalance,
        solValueUSD,
        csbtcValueUSD,
        totalValueUSD
      });

      console.log('✅ Actualización completada. Tokens:', tokensWithBalance.length);

    } catch (error) {
      console.error('❌ Error en getUserTokens:', error);

      // CRÍTICO: NO limpiar tokens en caso de error
      // En su lugar, mantener los tokens existentes
      if (lastValidTokensRef.current.length > 0) {
        console.log('🛡️ Manteniendo tokens existentes debido a error');
        // Solo actualizar precios si es posible
        try {
          const priceData = prices;
          const updatedTokens = lastValidTokensRef.current.map(token => ({
            ...token,
            price: token.symbol === 'CSBTC' ? priceData.csbtcPrice :
                   token.symbol === 'SOL' ? priceData.solPrice : token.price,
            valueUSD: token.uiAmount * (token.symbol === 'CSBTC' ? priceData.csbtcPrice :
                                       token.symbol === 'SOL' ? priceData.solPrice : token.price || 1)
          }));
          setUserTokens(updatedTokens);
        } catch (priceError) {
          // Si ni siquiera podemos actualizar precios, mantener tokens tal cual
          console.log('⚠️ No se pudieron actualizar precios');
        }
      }

      // Mostrar mensaje de error pero NO limpiar estado
      console.warn('⚠️ Error al actualizar tokens, pero manteniendo estado anterior');
    } finally {
      setLoading(false);
    }
  }, [
    currentWallet,
    findCSBTC,
    loadTokenPrices,
    processTokensForUI,
    prices,
    getSolBalanceSafe,
    getTokensSafe
    // NOTA: No incluimos userTokens en las dependencias para evitar loops
  ]);

  // Actualizar solo precios - FUNCIÓN SEGURA
  const refreshTokenPrices = useCallback(async () => {
    try {
      const newPriceData = await loadTokenPrices();

      // Actualizar precios en los tokens existentes sin resetear la lista
      if (lastValidTokensRef.current.length > 0) {
        const updatedTokens = lastValidTokensRef.current.map(token => ({
          ...token,
          price: token.symbol === 'CSBTC' ? newPriceData.csbtcPrice :
                 token.symbol === 'SOL' ? newPriceData.solPrice : token.price,
          valueUSD: token.uiAmount * (token.symbol === 'CSBTC' ? newPriceData.csbtcPrice :
                                     token.symbol === 'SOL' ? newPriceData.solPrice : token.price || 1),
          priceChange24h: token.symbol === 'CSBTC' ? newPriceData.csbtcPriceChange24h : token.priceChange24h
        }));

        setUserTokens(updatedTokens);
        lastValidTokensRef.current = updatedTokens;
      }

      return newPriceData;
    } catch (error) {
      console.error('Error refreshing token prices:', error);
      return prices; // Retornar precios actuales en caso de error
    }
  }, [loadTokenPrices, prices]);

  // Función para obtener datos específicos de CSBTC
  const getCSBTCData = useCallback(() => {
    const csbtcToken = userTokens.find(token => token.isCSBTC);

    return {
      balance: csbtcToken?.uiAmount || walletData.csbtcBalance,
      valueUSD: csbtcToken?.valueUSD || walletData.csbtcValueUSD,
      price: prices.csbtcPrice,
      priceChange24h: prices.csbtcPriceChange24h,
      volume24h: prices.csbtcVolume24h,
      exists: !!(csbtcToken || walletData.csbtcBalance > 0)
    };
  }, [userTokens, walletData, prices]);

  // Obtener datos del wallet
  const getWalletData = useCallback(() => {
    return walletData;
  }, [walletData]);

  // Forzar recarga - VERSIÓN MEJORADA
  const forceRefreshTokens = useCallback(async () => {
    if (currentWallet?.publicKey) {
      console.log('🔄 Forzando recarga completa de tokens...');
      await getUserTokens(currentWallet.publicKey);
    }
  }, [currentWallet, getUserTokens]);

  // Obtener tokens populares
  const getPopularTokens = useCallback(async () => {
    try {
      console.log('📊 Obteniendo tokens populares...');
      const response = await fetch('/popular-tokens');

      if (response.ok) {
        const data = await response.json();
        const tokens = data.tokens || [];

        // Asegurar que CSBTC esté en la lista
        if (!tokens.find(t => t.mint === '9EpY5vX7Jwc9z9v6un8HEpRuGWrMvWenVvcM1h1EXpmR')) {
          tokens.unshift({
            mint: '9EpY5vX7Jwc9z9v6un8HEpRuGWrMvWenVvcM1h1EXpmR',
            symbol: 'CSBTC',
            name: 'Crosschain Bitcoin',
            decimals: 6,
            uiAmount: 0
          });
        }

        const priceData = await loadTokenPrices();
        const enrichedTokens = processTokensForUI(tokens, priceData);
        setPopularTokens(enrichedTokens);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error obteniendo tokens populares:', error);
      // Fallback tokens
      const fallbackTokens = [
        {
          mint: '9EpY5vX7Jwc9z9v6un8HEpRuGWrMvWenVvcM1h1EXpmR',
          symbol: 'CSBTC',
          name: 'Crosschain Bitcoin',
          decimals: 6,
          uiAmount: 0
        },
        {
          mint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
          symbol: 'USDC',
          name: 'USD Coin',
          decimals: 6,
          uiAmount: 0
        },
        {
          mint: 'So11111111111111111111111111111111111111112',
          symbol: 'SOL',
          name: 'Solana',
          decimals: 9,
          uiAmount: 0
        },
        {
          mint: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
          symbol: 'USDT',
          name: 'Tether USD',
          decimals: 6,
          uiAmount: 0
        }
      ];

      const priceData = await loadTokenPrices();
      const enrichedTokens = processTokensForUI(fallbackTokens, priceData);
      setPopularTokens(enrichedTokens);
    }
  }, [loadTokenPrices, processTokensForUI]);

  // Efectos
  useEffect(() => {
    getPopularTokens();

    // Actualizar precios cada 2 minutos
    const priceInterval = setInterval(() => {
      refreshTokenPrices();
    }, 120000);

    return () => clearInterval(priceInterval);
  }, [getPopularTokens, refreshTokenPrices]);

  useEffect(() => {
    if (currentWallet) {
      getUserTokens();
    } else {
      console.log('👤 No hay wallet conectado, limpiando tokens...');
      // Solo limpiar si realmente no hay wallet
      setUserTokens([]);
      setWalletData({
        solBalance: 0,
        csbtcBalance: 0,
        solValueUSD: 0,
        csbtcValueUSD: 0,
        totalValueUSD: 0
      });
      lastValidTokensRef.current = [];
      lastPublicKeyRef.current = '';
    }
  }, [currentWallet]); // Removemos getUserTokens de las dependencias

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    // Tokens
    userTokens,
    popularTokens,

    // Datos del wallet
    walletData,

    // Precios
    prices,

    // Estados
    loading,

    // Funciones
    getUserTokens,
    getPopularTokens,
    refreshTokenPrices,
    getCSBTCData,
    getWalletData,
    forceRefreshTokens
  };
};
